import React, { useEffect, useMemo, useState } from 'react';
import { storage } from '../../utils/platform';
import { useInsights } from '../hooks/useInsights';
import {
  isInsufficientData,
  groupByCategory,
  topCategories,
  topActions,
  countByType,
} from '../utils/insights';
import InsightsHeader from '../sections/InsightsHeader';
import SentimentSection from '../sections/SentimentSection';
import CategoriesSection from '../sections/CategoriesSection';
import ActionsSection from '../sections/ActionsSection';
import TrendsSection from '../sections/TrendsSection';
import CategoryDetailModal from '../sections/CategoryDetailModal';
import EmptyState from '../components/EmptyState';
import { API_BASE } from '../../config/api';

export default function MobileInsights() {
  const [restaurantSnapshot, setRestaurantSnapshot] = useState(null);

  // Fetch léger : on a besoin du rps_score pour le SentimentScore (Option A)
  // et de l'id pour invalider le cache si on change de restaurant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await storage.get('token');
        if (!token) return;
        const res = await fetch(`${API_BASE}/api/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const d = await res.json();
        if (!cancelled) setRestaurantSnapshot(d?.restaurant || null);
      } catch (e) {
        console.warn('[MobileInsights] dashboard fetch failed:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const {
    data,
    loading,
    regenerating,
    error,
    fetchedAt,
    cooldownRemainingMs,
    regenerate,
  } = useInsights({ restaurantId: restaurantSnapshot?.id });

  const insights = useMemo(() => (Array.isArray(data?.insights) ? data.insights : []), [data]);
  const trends = useMemo(() => (Array.isArray(data?.trends) ? data.trends : []), [data]);
  const reviewsAnalyzed = data?.meta?.reviews_analyzed || 0;
  const feedbacksAnalyzed = data?.meta?.feedbacks_analyzed || 0;
  const insufficient = isInsufficientData(data);
  const allGroups = useMemo(() => groupByCategory(insights), [insights]);
  const top3 = useMemo(() => topCategories(insights, 3), [insights]);
  const actions = useMemo(() => topActions(insights, 3), [insights]);
  const breakdown = useMemo(() => countByType(insights), [insights]);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const groupsToShow = showAllCategories ? allGroups : top3;
  const score = restaurantSnapshot?.rps_score ?? null;

  return (
    <div style={styles.page}>
      <InsightsHeader
        reviewsAnalyzed={reviewsAnalyzed}
        feedbacksAnalyzed={feedbacksAnalyzed}
        fetchedAt={fetchedAt}
        onRegenerate={regenerate}
        regenerating={regenerating}
        cooldownRemainingMs={cooldownRemainingMs}
        fallbackMode={!!error && !data}
      />

      <div style={styles.content}>
        {loading && !data ? (
          <SkeletonInsights />
        ) : error && !data ? (
          <EmptyState
            emoji="⚠️"
            title="Analyse temporairement indisponible"
            subtitle={String(error)}
          />
        ) : insufficient ? (
          <EmptyState
            emoji="📊"
            title="Pas encore assez d'avis pour une analyse fiable"
            subtitle="Synchronisez Google ou collectez 5+ avis pour activer l'analyse IA."
          />
        ) : (
          <>
            <SentimentSection
              score={score != null ? score : 0}
              breakdown={breakdown}
            />

            <TrendsSection trends={trends} />

            <CategoriesSection
              groups={groupsToShow}
              onSelect={setSelectedGroup}
              totalGroups={allGroups.length}
              onSeeAll={() => setShowAllCategories(true)}
            />

            <ActionsSection actions={actions} />
          </>
        )}
      </div>

      <CategoryDetailModal
        group={selectedGroup}
        open={!!selectedGroup}
        onClose={() => setSelectedGroup(null)}
      />

      <style>{`
        @keyframes revyoPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

function SkeletonInsights() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ height: 200, background: '#1A1A1A', opacity: 0.15, borderRadius: 20, animation: 'revyoPulse 1.4s ease-in-out infinite' }} />
      {[1, 2, 3].map(i => (
        <div key={i} style={{ height: 180, background: '#E5E5E0', borderRadius: 16, animation: 'revyoPulse 1.4s ease-in-out infinite' }} />
      ))}
      <div style={{ height: 220, background: '#E5E5E0', borderRadius: 16, animation: 'revyoPulse 1.4s ease-in-out infinite' }} />
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100dvh',
    background: '#EDEAE3',
    paddingBottom: 32,
  },
  content: {
    padding: '12px 16px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
};
